import React, { useState, useEffect } from 'react';
import { BACKEND_URL } from '../config';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Label } from './ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from './ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from './ui/select';
import { 
  Users, 
  Plus, 
  Edit, 
  Trash2, 
  Mail, 
  Phone, 
  Calendar,
  Building2,
  UserCheck,
  Award,
  MapPin,
  Briefcase,
  Save,
  X,
  Eye,
  Shield,
  CheckCircle2
} from 'lucide-react';
import { toast } from 'sonner';
import StakeholderManagement from './StakeholderManagement';

const API = `${BACKEND_URL}/api`;

const TeamManagement = ({ projectId, user }) => {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(false);
  const [teamData, setTeamData] = useState({
    team_members: [],
    stakeholders: [],
    statistics: {},
    project_info: {}
  });
  
  // Dialog states
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [editingMember, setEditingMember] = useState(null);
  const [approvalDialogOpen, setApprovalDialogOpen] = useState(false);
  const [approvingMember, setApprovingMember] = useState(null);
  
  // Form state
  const [formData, setFormData] = useState({
    first_name: '',
    last_name: '',
    position: '',
    email: '',
    phone: '',
    department: '',
    bio: '',
    skills: '',
    responsibilities: '',
    affiliation: 'social_actor',
    organization_name: ''
  });

  useEffect(() => {
    fetchTeamData();
  }, [projectId]);

  const fetchTeamData = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/projects/${projectId}/team-overview`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      
      console.log('[TeamManagement] Fetched team data:', response.data);
      console.log('[TeamManagement] Team members with edit_status:', 
        response.data.team_members.filter(m => m.edit_status).map(m => ({
          name: `${m.first_name} ${m.last_name}`,
          status: m.edit_status
        }))
      );
      
      setTeamData(response.data);
    } catch (error) {
      console.error('Failed to fetch team data:', error);
      toast.error(t('team_management.load_failed'));
    } finally {
      setLoading(false);
    }
  };

  const handleAddMember = async (e) => {
    e.preventDefault();
    
    try {
      const memberData = {
        ...formData,
        skills: (formData.skills || '').split(',').map(s => s.trim()).filter(s => s),
        responsibilities: (formData.responsibilities || '').split(',').map(r => r.trim()).filter(r => r)
      };
      
      await axios.post(`${API}/projects/${projectId}/team-members`, memberData, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      
      toast.success('Team member added successfully!');
      setShowAddDialog(false);
      resetForm();
      fetchTeamData();
    } catch (error) {
      console.error('Failed to add team member:', error);
      if (error.response?.status === 403) {
        toast.error('You do not have permission to add team members');
      } else {
        toast.error('Failed to add team member');
      }
    }
  };

  const handleEditMember = async (e) => {
    e.preventDefault();
    
    try {
      const memberData = {
        ...formData,
        skills: (formData.skills || '').split(',').map(s => s.trim()).filter(s => s),
        responsibilities: (formData.responsibilities || '').split(',').map(r => r.trim()).filter(r => r)
      };
      
      await axios.patch(`${API}/projects/${projectId}/team-members/${editingMember.id}`, memberData, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      
      const statusMsg = user.role === 'social_actor' ? 
        'Team member edit submitted for approval!' : 
        'Team member updated successfully!';
      toast.success(statusMsg);
      
      setShowEditDialog(false);
      setEditingMember(null);
      resetForm();
      fetchTeamData();
    } catch (error) {
      console.error('Failed to update team member:', error);
      toast.error('Failed to update team member');
    }
  };

  const handleOpenApprovalDialog = (member) => {
    setApprovingMember(member);
    setApprovalDialogOpen(true);
  };

  const handleApproveEdit = async () => {
    try {
      await axios.post(
        `${API}/projects/${projectId}/team-members/${approvingMember.id}/approve-edit`,
        { approved: true },
        { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } }
      );
      
      toast.success('Team member edit approved successfully!');
      setApprovalDialogOpen(false);
      setApprovingMember(null);
      fetchTeamData();
    } catch (error) {
      console.error('Error approving team member:', error);
      toast.error(error.response?.data?.detail || 'Failed to approve team member edit');
    }
  };

  const handleRejectEdit = async () => {
    try {
      await axios.post(
        `${API}/projects/${projectId}/team-members/${approvingMember.id}/reject-edit`,
        { rejected: true },
        { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } }
      );
      
      toast.success('Team member edit rejected');
      setApprovalDialogOpen(false);
      setApprovingMember(null);
      fetchTeamData();
    } catch (error) {
      console.error('Error rejecting team member:', error);
      toast.error(error.response?.data?.detail || 'Failed to reject team member edit');
    }
  };

  const handleRemoveMember = async (memberId, memberName) => {
    if (!window.confirm(`Are you sure you want to remove ${memberName} from the team?`)) {
      return;
    }
    
    try {
      await axios.delete(`${API}/projects/${projectId}/team-members/${memberId}`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      
      toast.success('Team member removed successfully!');
      fetchTeamData();
    } catch (error) {
      console.error('Failed to remove team member:', error);
      toast.error('Failed to remove team member');
    }
  };

  const openEditDialog = (member) => {
    setEditingMember(member);
    setFormData({
      first_name: member.first_name || '',
      last_name: member.last_name || '',
      position: member.position || '',
      email: member.email || '',
      phone: member.phone || '',
      department: member.department || '',
      bio: member.bio || '',
      skills: (member.skills || []).join(', '),
      responsibilities: (member.responsibilities || []).join(', '),
      affiliation: member.affiliation || 'social_actor',
      organization_name: member.organization_name || ''
    });
    setShowEditDialog(true);
  };

  const resetForm = () => {
    setFormData({
      first_name: '',
      last_name: '',
      position: '',
      email: '',
      phone: '',
      department: '',
      bio: '',
      skills: '',
      responsibilities: '',
      affiliation: 'social_actor',
      organization_name: ''
    });
  };

  const canManageTeam = user?.role === 'admin' || user?.role === 'administrator' || user?.role === 'social_actor' || user?.role === 'project_manager';
  
  // Debug logging
  console.log('[TeamManagement] User object:', user);
  console.log('[TeamManagement] User role:', user?.role);
  console.log('[TeamManagement] canManageTeam:', canManageTeam);

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="w-8 h-8 border-4 border-blue-600 border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center justify-between">
            <div className="flex items-center space-x-2">
              <Users className="h-6 w-6 text-blue-600" />
              <span>{t('team_management.header_title')}</span>
            </div>
            {canManageTeam && (
              <Button onClick={() => setShowAddDialog(true)} data-testid="add-team-member-button">
                <Plus className="h-4 w-4 mr-2" />
                {t('team_management.add_team_member')}
              </Button>
            )}
          </CardTitle>
        </CardHeader>
      </Card>

      {/* Team Statistics */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">{t('team_management.stat_total_members')}</p>
                <p className="text-2xl font-bold text-blue-600">
                  {teamData.statistics?.total_members || 0}
                </p>
              </div>
              <Users className="h-8 w-8 text-blue-600 opacity-20" />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">{t('team_management.stat_departments')}</p>
                <p className="text-2xl font-bold text-green-700">
                  {teamData.statistics?.departments_count || 0}
                </p>
              </div>
              <Building2 className="h-8 w-8 text-green-700 opacity-20" />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Positions</p>
                <p className="text-2xl font-bold text-purple-600">
                  {teamData.statistics?.positions_count || 0}
                </p>
              </div>
              <Briefcase className="h-8 w-8 text-purple-600 opacity-20" />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Project Status</p>
                <p className="text-lg font-semibold text-orange-700 capitalize">
                  {teamData.project_info?.status || 'Unknown'}
                </p>
              </div>
              <Award className="h-8 w-8 text-orange-700 opacity-20" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Key Stakeholders */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center space-x-2">
            <Shield className="h-5 w-5 text-purple-600" />
            <span>Key Stakeholders</span>
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {teamData.stakeholders.map((stakeholder) => (
              <Card key={stakeholder.id} className="bg-gradient-to-r from-purple-50 to-blue-50">
                <CardContent className="p-4">
                  <div className="flex items-start justify-between">
                    <div>
                      <h4 className="font-semibold text-lg">{stakeholder.name}</h4>
                      <Badge className="bg-purple-100 text-purple-800 mb-2">
                        {stakeholder.role}
                      </Badge>
                      <div className="space-y-1 text-sm text-gray-600">
                        {stakeholder.email && (
                          <div className="flex items-center space-x-2">
                            <Mail className="h-4 w-4" />
                            <span>{stakeholder.email}</span>
                          </div>
                        )}
                        {stakeholder.organization && (
                          <div className="flex items-center space-x-2">
                            <Building2 className="h-4 w-4" />
                            <span>{stakeholder.organization}</span>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
            
            {teamData.stakeholders.length === 0 && (
              <div className="col-span-2 text-center py-8 text-gray-500">
                <Shield className="h-12 w-12 mx-auto mb-3 opacity-20" />
                <p>Stakeholder information will be populated from user databases</p>
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Team Members — grouped by affiliation */}
      <Card>
        <CardHeader>
          <CardTitle>{t('team_management.members_title')}</CardTitle>
          <p className="text-xs text-gray-500 mt-1">
            Team members are grouped by affiliation: Social Actor implementing team, Investor team, and relative Stakeholders.
          </p>
        </CardHeader>
        <CardContent className="space-y-6">
          {(() => {
            const renderMemberCard = (member) => (
              <Card key={member.id} className="hover:shadow-md transition-shadow">
                <CardContent className="p-4">
                  <div className="flex items-start justify-between mb-3">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-1">
                        <h4 className="font-semibold text-lg">
                          {member.first_name} {member.last_name}
                        </h4>
                        {member.edit_status === 'pending_approval' && (
                          <Badge variant="secondary" className="bg-yellow-100 text-yellow-800">
                            Edit Pending
                          </Badge>
                        )}
                      </div>
                      <p className="text-blue-600 font-medium">{member.position}</p>
                      {member.organization_name && (
                        <p className="text-xs text-gray-500 mt-0.5 italic">{member.organization_name}</p>
                      )}
                      {member.department && (
                        <Badge className="bg-gray-100 text-gray-700 text-xs mt-1">
                          {member.department}
                        </Badge>
                      )}
                    </div>
                    {canManageTeam && (
                      <div className="flex flex-col space-y-1">
                        {member.edit_status === 'pending_approval' &&
                         (user.role === 'project_manager' || user.role === 'administrator') && (
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => handleOpenApprovalDialog(member)}
                            className="text-green-700 hover:text-green-800"
                          >
                            <CheckCircle2 className="h-3 w-3 mr-1" />
                            Review
                          </Button>
                        )}
                        <div className="flex space-x-1">
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => openEditDialog(member)}
                            data-testid={`edit-member-${member.id}`}
                          >
                            <Edit className="h-3 w-3" />
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => handleRemoveMember(member.id, `${member.first_name} ${member.last_name}`)}
                            data-testid={`delete-member-${member.id}`}
                          >
                            <Trash2 className="h-3 w-3" />
                          </Button>
                        </div>
                      </div>
                    )}
                  </div>

                  <div className="space-y-2 text-sm">
                    <div className="flex items-center space-x-2">
                      <Mail className="h-4 w-4 text-gray-500" />
                      <span>{member.email}</span>
                    </div>

                    {member.phone && (
                      <div className="flex items-center space-x-2">
                        <Phone className="h-4 w-4 text-gray-500" />
                        <span>{member.phone}</span>
                      </div>
                    )}

                    {member.bio && (
                      <div className="mt-3">
                        <p className="text-xs text-gray-600 line-clamp-3">{member.bio}</p>
                      </div>
                    )}

                    {member.skills && member.skills.length > 0 && (
                      <div className="mt-3">
                        <p className="text-xs text-gray-500 mb-1">Skills:</p>
                        <div className="flex flex-wrap gap-1">
                          {member.skills.slice(0, 3).map((skill, index) => (
                            <Badge key={index} className="text-xs bg-blue-100 text-blue-700">
                              {skill}
                            </Badge>
                          ))}
                          {member.skills.length > 3 && (
                            <Badge className="text-xs bg-gray-100 text-gray-600">
                              +{member.skills.length - 3} more
                            </Badge>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                </CardContent>
              </Card>
            );

            const grouped = teamData.team_members_grouped || (() => {
              // Fallback: derive client-side if backend response is older
              const out = { social_actor: [], investor: [], stakeholder: [] };
              for (const m of teamData.team_members || []) {
                const k = m.affiliation || 'social_actor';
                (out[k] || out.social_actor).push(m);
              }
              return out;
            })();

            const sections = [
              {
                key: 'social_actor',
                title: 'Social Actor Team',
                description: 'Implementing organisation members directly delivering the project.',
                accent: 'border-l-4 border-emerald-500',
                tone: 'bg-emerald-50 text-emerald-800',
                Icon: Building2,
              },
              {
                key: 'investor',
                title: 'Investor Team',
                description: 'Members on the investor / funder side overseeing this engagement.',
                accent: 'border-l-4 border-indigo-500',
                tone: 'bg-indigo-50 text-indigo-800',
                Icon: Award,
              },
              {
                key: 'stakeholder',
                title: 'Relative Stakeholders',
                description: 'Third-party stakeholders involved in this project (municipalities, partners, beneficiary reps).',
                accent: 'border-l-4 border-amber-500',
                tone: 'bg-amber-50 text-amber-800',
                Icon: Shield,
              },
            ];

            const totalAcrossAll = sections.reduce(
              (sum, s) => sum + (grouped[s.key]?.length || 0),
              0
            );

            return (
              <>
                {totalAcrossAll === 0 && (
                  <div className="text-center py-6 mb-4 border border-dashed border-gray-300 rounded-lg bg-gray-50" data-testid="team-empty-state">
                    <Users className="h-12 w-12 mx-auto text-gray-300 mb-2" />
                    <h3 className="text-base font-medium text-gray-900 mb-1">No Team Members Yet</h3>
                    <p className="text-sm text-gray-600 mb-3">
                      {canManageTeam
                        ? 'Start building your team by adding the first member to one of the three groups below.'
                        : 'Team members will be added by project administrators and social actors.'}
                    </p>
                    {canManageTeam && (
                      <Button
                        onClick={() => setShowAddDialog(true)}
                        size="sm"
                        data-testid="empty-add-first-member-btn"
                      >
                        <Plus className="h-4 w-4 mr-2" />
                        Add First Team Member
                      </Button>
                    )}
                  </div>
                )}
                {sections.map((s) => {
                  const list = grouped[s.key] || [];
                  if (list.length === 0) {
                    return (
                      <section key={s.key} className={`${s.accent} pl-4`} data-testid={`team-section-${s.key}`}>
                        <div className="flex items-center gap-2 mb-2">
                          <s.Icon className="h-4 w-4 text-gray-500" />
                          <h3 className="text-base font-semibold text-gray-900">{s.title}</h3>
                          <Badge className={`${s.tone} text-xs border-0`} data-testid={`team-count-${s.key}`}>0</Badge>
                        </div>
                        <p className="text-xs text-gray-500 mb-2">{s.description}</p>
                        <p className="text-xs italic text-gray-500 py-2">No members in this group yet.</p>
                      </section>
                    );
                  }
                  return (
                    <section key={s.key} className={`${s.accent} pl-4`} data-testid={`team-section-${s.key}`}>
                      <div className="flex items-center gap-2 mb-2">
                        <s.Icon className="h-4 w-4 text-gray-500" />
                        <h3 className="text-base font-semibold text-gray-900">{s.title}</h3>
                        <Badge className={`${s.tone} text-xs border-0`} data-testid={`team-count-${s.key}`}>
                          {list.length}
                        </Badge>
                      </div>
                      <p className="text-xs text-gray-500 mb-3">{s.description}</p>
                      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                        {list.map(renderMemberCard)}
                      </div>
                    </section>
                  );
                })}
              </>
            );
          })()}
        </CardContent>
      </Card>

      {/* Add Team Member Dialog */}
      <Dialog open={showAddDialog} onOpenChange={setShowAddDialog}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Add New Team Member</DialogTitle>
            <DialogDescription>
              Add a person to this project. Tag them with the right affiliation so they appear in the correct group.
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleAddMember} className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="affiliation">Affiliation *</Label>
                <Select
                  value={formData.affiliation}
                  onValueChange={(v) => setFormData({ ...formData, affiliation: v })}
                >
                  <SelectTrigger id="affiliation" data-testid="add-affiliation-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="social_actor">Social Actor team</SelectItem>
                    <SelectItem value="investor">Investor team</SelectItem>
                    <SelectItem value="stakeholder">Relative stakeholder</SelectItem>
                  </SelectContent>
                </Select>
                <p className="text-xs text-gray-500">
                  Which side of the project does this person belong to?
                </p>
              </div>
              <div className="space-y-2">
                <Label htmlFor="organization_name">Organization (optional)</Label>
                <Input
                  id="organization_name"
                  placeholder="e.g., Impact Investment Fund / City of Athens"
                  value={formData.organization_name}
                  onChange={(e) => setFormData({ ...formData, organization_name: e.target.value })}
                  data-testid="add-organization-input"
                />
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="first_name">First Name *</Label>
                <Input
                  id="first_name"
                  value={formData.first_name}
                  onChange={(e) => setFormData({...formData, first_name: e.target.value})}
                  required
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="last_name">Last Name *</Label>
                <Input
                  id="last_name"
                  value={formData.last_name}
                  onChange={(e) => setFormData({...formData, last_name: e.target.value})}
                  required
                />
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="position">Position *</Label>
                <Input
                  id="position"
                  value={formData.position}
                  onChange={(e) => setFormData({...formData, position: e.target.value})}
                  required
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="department">Department</Label>
                <Input
                  id="department"
                  value={formData.department}
                  onChange={(e) => setFormData({...formData, department: e.target.value})}
                />
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="email">Email *</Label>
                <Input
                  id="email"
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({...formData, email: e.target.value})}
                  required
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="phone">Phone</Label>
                <Input
                  id="phone"
                  type="tel"
                  value={formData.phone}
                  onChange={(e) => setFormData({...formData, phone: e.target.value})}
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="bio">Bio</Label>
              <Textarea
                id="bio"
                value={formData.bio}
                onChange={(e) => setFormData({...formData, bio: e.target.value})}
                placeholder="Brief description of the team member's background and expertise..."
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="skills">Skills (comma-separated)</Label>
              <Input
                id="skills"
                value={formData.skills}
                onChange={(e) => setFormData({...formData, skills: e.target.value})}
                placeholder="e.g., Project Management, Digital Training, Community Engagement"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="responsibilities">Responsibilities (comma-separated)</Label>
              <Input
                id="responsibilities"
                value={formData.responsibilities}
                onChange={(e) => setFormData({...formData, responsibilities: e.target.value})}
                placeholder="e.g., Training coordination, Stakeholder management, Progress monitoring"
              />
            </div>

            <div className="flex justify-end space-x-2 pt-4">
              <Button type="button" variant="outline" onClick={() => setShowAddDialog(false)}>
                Cancel
              </Button>
              <Button type="submit">
                <Save className="h-4 w-4 mr-2" />
                Add Team Member
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Edit Team Member Dialog */}
      <Dialog open={showEditDialog} onOpenChange={setShowEditDialog}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Edit Team Member</DialogTitle>
            <DialogDescription>
              Update this member's details. Changing the affiliation will move them to the matching section.
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleEditMember} className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="edit_affiliation">Affiliation *</Label>
                <Select
                  value={formData.affiliation}
                  onValueChange={(v) => setFormData({ ...formData, affiliation: v })}
                >
                  <SelectTrigger id="edit_affiliation" data-testid="edit-affiliation-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="social_actor">Social Actor team</SelectItem>
                    <SelectItem value="investor">Investor team</SelectItem>
                    <SelectItem value="stakeholder">Relative stakeholder</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label htmlFor="edit_organization_name">Organization (optional)</Label>
                <Input
                  id="edit_organization_name"
                  placeholder="e.g., Impact Investment Fund / City of Athens"
                  value={formData.organization_name}
                  onChange={(e) => setFormData({ ...formData, organization_name: e.target.value })}
                  data-testid="edit-organization-input"
                />
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="edit_first_name">First Name *</Label>
                <Input
                  id="edit_first_name"
                  value={formData.first_name}
                  onChange={(e) => setFormData({...formData, first_name: e.target.value})}
                  required
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="edit_last_name">Last Name *</Label>
                <Input
                  id="edit_last_name"
                  value={formData.last_name}
                  onChange={(e) => setFormData({...formData, last_name: e.target.value})}
                  required
                />
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="edit_position">Position *</Label>
                <Input
                  id="edit_position"
                  value={formData.position}
                  onChange={(e) => setFormData({...formData, position: e.target.value})}
                  required
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="edit_department">Department</Label>
                <Input
                  id="edit_department"
                  value={formData.department}
                  onChange={(e) => setFormData({...formData, department: e.target.value})}
                />
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="edit_email">Email *</Label>
                <Input
                  id="edit_email"
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({...formData, email: e.target.value})}
                  required
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="edit_phone">Phone</Label>
                <Input
                  id="edit_phone"
                  type="tel"
                  value={formData.phone}
                  onChange={(e) => setFormData({...formData, phone: e.target.value})}
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="edit_bio">Bio</Label>
              <Textarea
                id="edit_bio"
                value={formData.bio}
                onChange={(e) => setFormData({...formData, bio: e.target.value})}
                placeholder="Brief description of the team member's background and expertise..."
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="edit_skills">Skills (comma-separated)</Label>
              <Input
                id="edit_skills"
                value={formData.skills}
                onChange={(e) => setFormData({...formData, skills: e.target.value})}
                placeholder="e.g., Project Management, Digital Training, Community Engagement"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="edit_responsibilities">Responsibilities (comma-separated)</Label>
              <Input
                id="edit_responsibilities"
                value={formData.responsibilities}
                onChange={(e) => setFormData({...formData, responsibilities: e.target.value})}
                placeholder="e.g., Training coordination, Stakeholder management, Progress monitoring"
              />
            </div>

            <div className="flex justify-end space-x-2 pt-4">
              <Button type="button" variant="outline" onClick={() => setShowEditDialog(false)}>
                Cancel
              </Button>
              <Button type="submit">
                <Save className="h-4 w-4 mr-2" />
                {user.role === 'social_actor' ? 'Submit for Approval' : 'Update Team Member'}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Approval Review Dialog */}
      <Dialog open={approvalDialogOpen} onOpenChange={setApprovalDialogOpen}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Review Team Member Edit</DialogTitle>
          </DialogHeader>
          {approvingMember && (
            <div className="space-y-4">
              <div className="bg-blue-50 border border-blue-200 p-4 rounded-lg">
                <h3 className="font-semibold text-lg mb-1">
                  {approvingMember.first_name} {approvingMember.last_name}
                </h3>
                <p className="text-sm text-gray-600">{approvingMember.position}</p>
              </div>

              {approvingMember.pending_changes && (
                <div className="space-y-3">
                  <h4 className="font-semibold text-gray-900">Proposed Changes:</h4>
                  <div className="bg-white border rounded-lg divide-y">
                    {Object.entries(approvingMember.pending_changes).map(([field, newValue]) => {
                      // Skip internal fields
                      if (field === 'updated_at') return null;
                      
                      const oldValue = approvingMember[field];
                      const fieldLabels = {
                        first_name: 'First Name',
                        last_name: 'Last Name',
                        position: 'Position',
                        department: 'Department',
                        email: 'Email',
                        phone: 'Phone',
                        bio: 'Bio',
                        skills: 'Skills',
                        responsibilities: 'Responsibilities'
                      };
                      
                      const label = fieldLabels[field] || field;

                      // Handle array fields (skills, responsibilities)
                      if (Array.isArray(newValue) || Array.isArray(oldValue)) {
                        const oldArray = Array.isArray(oldValue) ? oldValue : [];
                        const newArray = Array.isArray(newValue) ? newValue : [];
                        
                        if (JSON.stringify(oldArray) !== JSON.stringify(newArray)) {
                          return (
                            <div key={field} className="p-4">
                              <p className="text-sm font-medium text-gray-700 mb-2">{label}</p>
                              <div className="grid grid-cols-2 gap-4">
                                <div>
                                  <p className="text-xs font-medium text-gray-500 mb-1">Current:</p>
                                  <div className="bg-red-50 p-2 rounded text-sm text-gray-700 min-h-[60px]">
                                    {oldArray.length > 0 ? (
                                      <ul className="list-disc list-inside space-y-1">
                                        {oldArray.map((item, idx) => (
                                          <li key={idx} className="text-xs">{item}</li>
                                        ))}
                                      </ul>
                                    ) : (
                                      <span className="italic text-gray-500">Empty</span>
                                    )}
                                  </div>
                                </div>
                                <div>
                                  <p className="text-xs font-medium text-gray-500 mb-1">New:</p>
                                  <div className="bg-green-50 p-2 rounded text-sm text-gray-700 min-h-[60px]">
                                    {newArray.length > 0 ? (
                                      <ul className="list-disc list-inside space-y-1">
                                        {newArray.map((item, idx) => (
                                          <li key={idx} className="text-xs">{item}</li>
                                        ))}
                                      </ul>
                                    ) : (
                                      <span className="italic text-gray-500">Empty</span>
                                    )}
                                  </div>
                                </div>
                              </div>
                            </div>
                          );
                        }
                        return null;
                      }

                      // Handle other fields
                      if (String(oldValue) !== String(newValue)) {
                        return (
                          <div key={field} className="p-4">
                            <p className="text-sm font-medium text-gray-700 mb-2">{label}</p>
                            <div className="grid grid-cols-2 gap-4">
                              <div>
                                <p className="text-xs font-medium text-gray-500 mb-1">Current:</p>
                                <p className="text-sm bg-red-50 p-2 rounded text-gray-700">
                                  {String(oldValue) || <span className="italic text-gray-500">Empty</span>}
                                </p>
                              </div>
                              <div>
                                <p className="text-xs font-medium text-gray-500 mb-1">New:</p>
                                <p className="text-sm bg-green-50 p-2 rounded text-gray-700">
                                  {String(newValue) || <span className="italic text-gray-500">Empty</span>}
                                </p>
                              </div>
                            </div>
                          </div>
                        );
                      }
                      return null;
                    })}
                  </div>
                </div>
              )}

              <div className="flex justify-end space-x-2 pt-4 border-t">
                <Button
                  variant="outline"
                  onClick={() => {
                    setApprovalDialogOpen(false);
                    setApprovingMember(null);
                  }}
                >
                  Cancel
                </Button>
                <Button
                  variant="destructive"
                  onClick={handleRejectEdit}
                  className="bg-red-600 hover:bg-red-700"
                >
                  Reject Changes
                </Button>
                <Button
                  onClick={handleApproveEdit}
                  className="bg-green-700 hover:bg-green-800"
                >
                  Approve Changes
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Stakeholder Management Section */}
      <StakeholderManagement projectId={projectId} user={user} />
    </div>
  );
};

export default TeamManagement;